// src/MarketingStats.js
import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/apiUtils';
import { 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Box,
  InputAdornment,
  useMediaQuery,
  Snackbar,
  Alert
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, parse, addDays } from 'date-fns';
import { enUS } from 'date-fns/locale';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';

// Styling constants
const styles = {
  formContainer: {
    marginBottom: '2rem',
    padding: '1.5rem',
  },
  tableContainer: {
    marginTop: '1rem',
    overflowX: 'auto',
  },
  formRow: {
    marginBottom: '1rem',
  },
  currencyField: {
    '& input': {
      textAlign: 'right',
    },
  },
  actionCell: {
    whiteSpace: 'nowrap',
  },
  listViewHeader: {
    marginTop: '2rem',
    marginBottom: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  mobileDateCell: {
    whiteSpace: 'nowrap',
  }
};

const MarketingStats = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Form state
  const [formData, setFormData] = useState({
    source: '',
    itemDate: new Date(), // Default to today (Eastern Time)
    endDate: new Date(), // Default to today (Eastern Time)
    county: '',
    numMessages: '',
    numLsaCalls: '',
    numOtherCalls: '',
    adBudget: '',
    avgCostPerLead: '',
    totalSpend: '',
    reach: '',
    impressions: '',
    ctr: '',
    frequency: ''
  });

  // State for existing records
  const [records, setRecords] = useState([]);
  
  // State for edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  
  // Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');

  // Load data on component mount
  useEffect(() => {
    fetchRecords();
  }, []);

  // Fetch marketing leads data
  const fetchRecords = async () => {
    try {
      const response = await apiFetch('/api/reports/marketing-leads');
      
      if (!response.ok) {
        throw new Error(`Error fetching records: ${response.status}`);
      }
      
      const data = await response.json();
      setRecords(data);
    } catch (error) {
      console.error('Error fetching records:', error);
      showAlert('Failed to load marketing statistics data', 'error');
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for source field
    if (name === 'source') {
      // If switching to Facebook, reset LSA and Other Calls to empty string
      if (value === 'Facebook') {
        setFormData({
          ...formData,
          source: value,
          numLsaCalls: '',
          numOtherCalls: ''
        });
      } else {
        setFormData({ ...formData, source: value });
      }
    }
    // Handle numeric fields
    else if (['numMessages', 'numLsaCalls', 'numOtherCalls', 'reach', 'impressions', 'ctr', 'frequency'].includes(name)) {
      // Allow empty strings, but convert valid inputs to integers
      const intValue = value === '' ? '' : (parseInt(value, 10) || 0);
      setFormData({ ...formData, [name]: intValue });
    } else if (['adBudget', 'avgCostPerLead', 'totalSpend'].includes(name)) {
      // Allow empty strings, but convert valid inputs to floats
      const floatValue = value === '' ? '' : (parseFloat(value) || 0);
      setFormData({ ...formData, [name]: floatValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Handle date changes
  const handleStartDateChange = (date) => {
    setFormData({ ...formData, itemDate: date });
  };

  const handleEndDateChange = (date) => {
    setFormData({ ...formData, endDate: date });
  };

  // Format currency for display
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  // Submit form data
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.source || !formData.itemDate || !formData.county) {
      showAlert('Please fill in all required fields', 'error');
      return;
    }
    
    try {
      const url = isEditing 
        ? `/api/reports/marketing-leads/${currentId}`
        : `/api/reports/marketing-leads`;
      
      const method = isEditing ? 'PUT' : 'POST';
      
      // Format dates to ISO string for API
      const formattedData = {
        ...formData,
        itemDate: format(formData.itemDate, 'yyyy-MM-dd'),
        endDate: format(formData.endDate, 'yyyy-MM-dd')
      };
      
      const response = await apiFetch(url, {
        method: method,
        body: JSON.stringify(formattedData)
      });
      
      if (!response.ok) {
        throw new Error(`Error saving record: ${response.status}`);
      }
      
      // Reset form and refresh data
      resetForm();
      fetchRecords();
      
      showAlert(isEditing ? 'Record updated successfully' : 'Record added successfully', 'success');
    } catch (error) {
      console.error('Error saving record:', error);
      showAlert('Failed to save record', 'error');
    }
  };

  // Reset form to default values
  const resetForm = () => {
    setFormData({
      source: '',
      itemDate: new Date(), // Default to today
      endDate: new Date(), // Default to today
      county: '',
      numMessages: '',
      numLsaCalls: '',
      numOtherCalls: '',
      adBudget: '',
      avgCostPerLead: '',
      totalSpend: '',
      reach: '',
      impressions: '',
      ctr: '',
      frequency: ''
    });
    setIsEditing(false);
    setCurrentId(null);
  };

  // Edit record
  const handleEdit = (record) => {
    // Parse the date string from record
    const itemDate = parse(record.itemDate, 'yyyy-MM-dd', new Date());
    // Parse the end date string from record (if it exists)
    const endDate = record.endDate ? parse(record.endDate, 'yyyy-MM-dd', new Date()) : new Date();
    
    setFormData({
      source: record.source,
      itemDate: itemDate,
      endDate: endDate,
      county: record.county,
      numMessages: record.numMessages,
      numLsaCalls: record.numLsaCalls,
      numOtherCalls: record.numOtherCalls,
      adBudget: record.adBudget,
      avgCostPerLead: record.avgCostPerLead,
      totalSpend: record.totalSpend,
      reach: record.reach || '',
      impressions: record.impressions || '',
      ctr: record.ctr || '',
      frequency: record.frequency || ''
    });
    
    setIsEditing(true);
    setCurrentId(record.id);
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (id) => {
    setRecordToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRecordToDelete(null);
  };

  // Delete record
  const handleDelete = async () => {
    try {
      const response = await apiFetch(`/api/reports/marketing-leads/${recordToDelete}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`Error deleting record: ${response.status}`);
      }
      
      // Close dialog and refresh data
      closeDeleteDialog();
      fetchRecords();
      
      showAlert('Record deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting record:', error);
      showAlert('Failed to delete record', 'error');
      closeDeleteDialog();
    }
  };

  // Show alert
  const showAlert = (message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  // Close alert
  const handleAlertClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlertOpen(false);
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Daily Marketing Statistics
      </Typography>
      
      {/* Form Section */}
      <Paper sx={styles.formContainer}>
        <Typography variant="h6" gutterBottom>
          {isEditing ? 'Edit Marketing Data' : 'Add Marketing Data'}
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Source field - dropdown with Facebook or LSA options */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel id="source-label">Source</InputLabel>
                <Select
                  labelId="source-label"
                  name="source"
                  value={formData.source}
                  onChange={handleInputChange}
                  label="Source"
                >
                  <MenuItem value="Facebook">Facebook</MenuItem>
                  <MenuItem value="LSA (Google)">LSA (Google)</MenuItem>
                  <MenuItem value="Google Search">Google Search</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Start Date field */}
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
                <DatePicker
                  label="Start Date"
                  value={formData.itemDate}
                  onChange={handleStartDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                  format="MM/dd/yyyy"
                />
              </LocalizationProvider>
            </Grid>

            {/* End Date field */}
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
                <DatePicker
                  label="End Date"
                  value={formData.endDate}
                  onChange={handleEndDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  format="MM/dd/yyyy"
                />
              </LocalizationProvider>
            </Grid>
            
            {/* County field - dropdown with specified options */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel id="county-label">Service Area</InputLabel>
                <Select
                  labelId="county-label"
                  name="county"
                  value={formData.county}
                  onChange={handleInputChange}
                  label="County"
                >
                  <MenuItem value="Lakeland">Lakeland</MenuItem>
                  <MenuItem value="N. Hillsborough">N. Hillsborough</MenuItem>
                  <MenuItem value="Pinellas"  >Pinellas</MenuItem>
                  <MenuItem value="Pasco">Pasco</MenuItem>
                  <MenuItem value="Sarasota">Sarasota</MenuItem>
                  <MenuItem value="S. Hillsborough">S. Hillsborough</MenuItem>
                  <MenuItem value="Springhill">Springhill</MenuItem>
                  <MenuItem value="Wesley Chapel">Wesley Chapel</MenuItem>
                  <MenuItem value="High Priority Growth">High Priority Growth</MenuItem>
                  <MenuItem value="Mid Level Growth">Mid Level Growth</MenuItem>
                  <MenuItem value="Established Locations">Established Locations</MenuItem>
                  <MenuItem value="All Service Areas">All Service Areas</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Number fields */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Number of Messages"
                name="numMessages"
                type="number"
                value={formData.numMessages}
                onChange={handleInputChange}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Number of LSA Calls"
                name="numLsaCalls"
                type="number"
                value={formData.numLsaCalls}
                onChange={handleInputChange}
                disabled={formData.source === 'Facebook'}
                InputProps={{ 
                  inputProps: { min: 0 },
                }}
                helperText={formData.source === 'Facebook' ? 'Not applicable for Facebook' : ''}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Number of Other Calls"
                name="numOtherCalls"
                type="number"
                value={formData.numOtherCalls}
                onChange={handleInputChange}
                disabled={formData.source === 'Facebook'}
                InputProps={{ 
                  inputProps: { min: 0 } 
                }}
                helperText={formData.source === 'Facebook' ? 'Not applicable for Facebook' : ''}
              />
            </Grid>
            
            {/* Currency fields */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Ad Budget"
                name="adBudget"
                type="number"
                value={formData.adBudget}
                onChange={handleInputChange}
                sx={styles.currencyField}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { min: 0, step: "0.01" }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Average Cost Per Lead"
                name="avgCostPerLead"
                type="number"
                value={formData.avgCostPerLead}
                onChange={handleInputChange}
                sx={styles.currencyField}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { min: 0, step: "0.01" }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Total Spend"
                name="totalSpend"
                type="number"
                value={formData.totalSpend}
                onChange={handleInputChange}
                sx={styles.currencyField}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { min: 0, step: "0.01" }
                }}
              />
            </Grid>

            {/* Google Search specific fields */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Reach"
                name="reach"
                type="number"
                value={formData.reach}
                onChange={handleInputChange}
                disabled={formData.source !== 'Google Search'}
                InputProps={{ 
                  inputProps: { min: 0 }
                }}
                helperText={formData.source !== 'Google Search' ? 'Only applicable for Google Search' : ''}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Impressions"
                name="impressions"
                type="number"
                value={formData.impressions}
                onChange={handleInputChange}
                disabled={formData.source !== 'Google Search'}
                InputProps={{ 
                  inputProps: { min: 0 }
                }}
                helperText={formData.source !== 'Google Search' ? 'Only applicable for Google Search' : ''}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="CTR"
                name="ctr"
                type="number"
                value={formData.ctr}
                onChange={handleInputChange}
                disabled={formData.source !== 'Google Search'}
                InputProps={{ 
                  inputProps: { min: 0 }
                }}
                helperText={formData.source !== 'Google Search' ? 'Only applicable for Google Search' : ''}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Frequency"
                name="frequency"
                type="number"
                value={formData.frequency}
                onChange={handleInputChange}
                disabled={formData.source !== 'Google Search'}
                InputProps={{ 
                  inputProps: { min: 0 }
                }}
                helperText={formData.source !== 'Google Search' ? 'Only applicable for Google Search' : ''}
              />
            </Grid>
            
            {/* Form buttons */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                >
                  {isEditing ? 'Update' : 'Save'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      {/* List View Section */}
      <Box sx={styles.listViewHeader}>
        <Typography variant="h6">
          Recent Marketing Statistics
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={resetForm} // Reset form to add a new entry
        >
          Add New
        </Button>
      </Box>
      
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              {!isMobile && <TableCell>ID</TableCell>}
              <TableCell>Start Date</TableCell>
              {!isMobile && <TableCell>End Date</TableCell>}
              <TableCell>Source</TableCell>
              <TableCell>Service Area</TableCell>
              {!isMobile && <TableCell align="right">Messages</TableCell>}
              {!isMobile && <TableCell align="right">LSA Calls</TableCell>}
              {!isMobile && <TableCell align="right">Other Calls</TableCell>}
              <TableCell align="right">Budget</TableCell>
              {!isMobile && <TableCell align="right">Avg. Cost/Lead</TableCell>}
              <TableCell align="right">Total Spend</TableCell>
              {!isMobile && <TableCell align="right">Reach</TableCell>}
              {!isMobile && <TableCell align="right">Impressions</TableCell>}
              {!isMobile && <TableCell align="right">CTR</TableCell>}
              {!isMobile && <TableCell align="right">Frequency</TableCell>}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isMobile ? 6 : 15} align="center">
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              records.map((record) => (
                <TableRow key={record.id}>
                  {!isMobile && <TableCell>{record.id}</TableCell>}
                  <TableCell sx={styles.mobileDateCell}>
                    {format(parse(record.itemDate, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy')}
                  </TableCell>
                  {!isMobile && <TableCell sx={styles.mobileDateCell}>
                    {record.endDate ? format(parse(record.endDate, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy') : ''}
                  </TableCell>}
                  <TableCell>{record.source}</TableCell>
                  <TableCell>{record.county}</TableCell>
                  {!isMobile && <TableCell align="right">{record.numMessages}</TableCell>}
                  {!isMobile && <TableCell align="right">{record.numLsaCalls}</TableCell>}
                  {!isMobile && <TableCell align="right">{record.numOtherCalls}</TableCell>}
                  <TableCell align="right">{formatCurrency(record.adBudget)}</TableCell>
                  {!isMobile && <TableCell align="right">{formatCurrency(record.avgCostPerLead)}</TableCell>}
                  <TableCell align="right">{formatCurrency(record.totalSpend)}</TableCell>
                  {!isMobile && <TableCell align="right">{record.reach}</TableCell>}
                  {!isMobile && <TableCell align="right">{record.impressions}</TableCell>}
                  {!isMobile && <TableCell align="right">{record.ctr}</TableCell>}
                  {!isMobile && <TableCell align="right">{record.frequency}</TableCell>}
                  <TableCell align="center" sx={styles.actionCell}>
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(record)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => openDeleteDialog(record.id)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this marketing statistics record? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Alert Snackbar */}
      <Snackbar open={alertOpen} autoHideDuration={6000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity={alertSeverity} sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MarketingStats;